/**
 * Main 3D scene component
 */

import { useEffect, useRef, useCallback, useState } from "react";
import { Canvas, useThree } from "@react-three/fiber";
import { OrbitControls, Grid } from "@react-three/drei";
import * as THREE from "three";
import {
  useMeshes,
  useCamera,
  useBackgroundColor,
  useViewerStore,
  useIKEnabled,
  useIKGizmos,
  useHiddenGizmoIds,
  useHighlightedMeshUuids,
  useCollidingMeshUuids,
  useEndCoordsGizmoVisible,
  useSelectedModuleRootLink,
  useInstances,
  useJoints,
  useHoverConnectionTarget,
  useSelectedModuleInstanceId,
} from "../store";
import { useWebSocket } from "../WebSocket";
import { recordMove, recordConnect, recordDisconnect, recordDelete, recordJointBatch } from "../hooks";
import { SceneMesh } from "./SceneMesh";
import { IKGizmo } from "./IKGizmo";
import { EndCoordsGizmo } from "./EndCoordsGizmo";
import { JointAxisVisualization } from "./JointAxisVisualization";
import { ConnectionPointMarkers } from "./ConnectionPointMarkers";

// Store scene utilities for external access
interface SceneUtils {
  getGroundPosition: (screenX: number, screenY: number) => [number, number, number] | null;
}

let sceneUtils: SceneUtils | null = null;

export function getSceneUtils(): SceneUtils | null {
  return sceneUtils;
}

// Position range limit (4m x 4m = -2 to 2)
const POSITION_LIMIT = 2;

function CameraController() {
  const camera = useCamera();
  const { camera: threeCamera } = useThree();

  useEffect(() => {
    threeCamera.position.set(...camera.position);
    threeCamera.lookAt(new THREE.Vector3(...camera.target));
  }, [camera, threeCamera]);

  return null;
}

function SceneUtilsProvider() {
  const { camera, gl } = useThree();
  const raycaster = useRef(new THREE.Raycaster());
  // Z-up coordinate system: ground plane is XY at Z=0
  const groundPlane = useRef(new THREE.Plane(new THREE.Vector3(0, 0, 1), 0));

  const getGroundPosition = useCallback(
    (screenX: number, screenY: number): [number, number, number] | null => {
      const rect = gl.domElement.getBoundingClientRect();
      const x = ((screenX - rect.left) / rect.width) * 2 - 1;
      const y = -((screenY - rect.top) / rect.height) * 2 + 1;

      raycaster.current.setFromCamera(new THREE.Vector2(x, y), camera);

      const intersection = new THREE.Vector3();
      const result = raycaster.current.ray.intersectPlane(groundPlane.current, intersection);

      if (result) {
        return [intersection.x, intersection.y, intersection.z];
      }
      return null;
    },
    [camera, gl]
  );

  useEffect(() => {
    sceneUtils = { getGroundPosition };
    return () => {
      sceneUtils = null;
    };
  }, [getGroundPosition]);

  return null;
}

interface DragState {
  isDragging: boolean;
  instanceId: string | null;
  offset: THREE.Vector3;
}

function DragHandler({
  children,
  onDragStateChange,
  disabled,
}: {
  children: React.ReactNode;
  onDragStateChange: (isDragging: boolean) => void;
  disabled?: boolean;
}) {
  const { camera, gl, raycaster } = useThree();
  const meshes = useMeshes();
  const rootLink = useSelectedModuleRootLink();
  const instances = useInstances();
  const hoverConnectionTarget = useHoverConnectionTarget();
  const {
    sendUpdateInstancePosition,
    sendTranslationEnd,
    sendSelectInstance,
    sendConnectModules,
  } = useWebSocket();
  const setSelectedMesh = useViewerStore((state) => state.setSelectedMesh);
  const setDragging = useViewerStore((state) => state.setDragging);
  const setDragOffset = useViewerStore((state) => state.setDragOffset);

  const dragState = useRef<DragState>({
    isDragging: false,
    instanceId: null,
    offset: new THREE.Vector3(),
  });

  // The position of the instance at the start of the drag
  const dragStartInstancePos = useRef<[number, number, number]>([0, 0, 0]);

  // Store refs to avoid stale closures
  const rootLinkRef = useRef(rootLink);
  const instancesRef = useRef(instances);
  const hoverConnectionTargetRef = useRef(hoverConnectionTarget);
  useEffect(() => {
    rootLinkRef.current = rootLink;
  }, [rootLink]);
  useEffect(() => {
    instancesRef.current = instances;
  }, [instances]);
  useEffect(() => {
    hoverConnectionTargetRef.current = hoverConnectionTarget;
  }, [hoverConnectionTarget]);

  // Z-up: ground plane is XY at Z=0
  const groundPlane = useRef(new THREE.Plane(new THREE.Vector3(0, 0, 1), 0));

  // Check if point is inside canvas
  const isInsideCanvas = useCallback(
    (clientX: number, clientY: number): boolean => {
      const rect = gl.domElement.getBoundingClientRect();
      return (
        clientX >= rect.left &&
        clientX <= rect.right &&
        clientY >= rect.top &&
        clientY <= rect.bottom
      );
    },
    [gl]
  );

  const getPlaneIntersection = useCallback(
    (clientX: number, clientY: number): THREE.Vector3 | null => {
      const rect = gl.domElement.getBoundingClientRect();
      const x = ((clientX - rect.left) / rect.width) * 2 - 1;
      const y = -((clientY - rect.top) / rect.height) * 2 + 1;

      raycaster.setFromCamera(new THREE.Vector2(x, y), camera);

      const intersection = new THREE.Vector3();
      const result = raycaster.ray.intersectPlane(groundPlane.current, intersection);

      return result ? intersection : null;
    },
    [camera, gl, raycaster]
  );

  const handlePointerMove = useCallback(
    (event: MouseEvent) => {
      if (!dragState.current.isDragging || !dragState.current.instanceId) return;

      // If mouse is outside canvas, do nothing (keep last position)
      if (!isInsideCanvas(event.clientX, event.clientY)) {
        return;
      }

      const intersection = getPlaneIntersection(event.clientX, event.clientY);
      if (!intersection) return;

      // Apply offset
      intersection.sub(dragState.current.offset);

      // Clamp to position limits (4m x 4m range)
      const clampedX = Math.max(-POSITION_LIMIT, Math.min(POSITION_LIMIT, intersection.x));
      const clampedY = Math.max(-POSITION_LIMIT, Math.min(POSITION_LIMIT, intersection.y));

      // Apply local offset instantly (no server round-trip).
      // All instances in the drag group (parent + descendants) share this offset.
      const startPos = dragStartInstancePos.current;
      setDragOffset([
        clampedX - startPos[0],
        clampedY - startPos[1],
        0,
      ]);
    },
    [
      getPlaneIntersection,
      isInsideCanvas,
      setDragOffset,
    ]
  );

  const handlePointerUp = useCallback(() => {
    if (dragState.current.isDragging) {
      const instanceId = dragState.current.instanceId;

      // Compute the final absolute position from start + current offset
      const store = useViewerStore.getState();
      const offset = store.dragOffset;
      const startPos = dragStartInstancePos.current;
      const finalPosition: [number, number, number] = [
        startPos[0] + offset[0],
        startPos[1] + offset[1],
        startPos[2] + offset[2],
      ];

      // Clear drag state in store (bakes offset into mesh positions)
      setDragging(null);

      // Send final position to server
      if (instanceId) {
        sendUpdateInstancePosition(instanceId, finalPosition);
      }

      // Check if we should connect (drag-to-connect via hover target)
      const target = hoverConnectionTargetRef.current;
      if (target && instanceId) {
        // Use the DRAGGED instance as the child, not the tree root.
        // This allows reconnecting a module that's already part of a tree.
        // The backend will disconnect it from its current parent and reconnect.
        const childLinkName = rootLinkRef.current?.name ?? "base_link";

        console.log("[DragHandler] Connecting on drop (using hover target):", {
          childInstanceId: instanceId,
          childLinkName,
          parentInstanceId: target.parentInstanceId,
          parentLinkName: target.parentLinkName,
        });

        // Connect the modules (undo = disconnect, instant)
        recordConnect(instanceId);
        sendConnectModules(
          instanceId,
          childLinkName,
          target.parentInstanceId,
          target.parentLinkName
        );
      }

      dragState.current.isDragging = false;
      dragState.current.instanceId = null;
      onDragStateChange(false);
      document.body.style.cursor = "default";

      // Notify backend that translation ended (to refresh IK gizmos)
      sendTranslationEnd();

      // Remove document-level listeners
      document.removeEventListener("mousemove", handlePointerMove);
      document.removeEventListener("mouseup", handlePointerUp);
    }
  }, [
    onDragStateChange,
    handlePointerMove,
    sendTranslationEnd,
    sendConnectModules,
    sendUpdateInstancePosition,
    setDragging,
  ]);

  // Start drag from a specific instance
  const startDrag = useCallback(
    (instanceId: string, meshUuid: string, event: PointerEvent) => {
      // Don't start drag if disabled (e.g., when end coords gizmo is active)
      if (disabled) return;

      const intersection = getPlaneIntersection(event.clientX, event.clientY);
      if (!intersection) return;

      // Get the current position of the instance from a mesh
      const meshData = meshes[meshUuid];
      if (!meshData) return;

      // Calculate offset from click point to mesh position
      const meshPos = new THREE.Vector3(...meshData.position);
      dragState.current.offset.copy(intersection).sub(meshPos);
      dragState.current.offset.z = 0; // Keep Z offset at 0

      dragState.current.isDragging = true;
      dragState.current.instanceId = instanceId;
      onDragStateChange(true);
      document.body.style.cursor = "move";

      // Record the instance position for undo
      const inst = instancesRef.current.find((i) => i.instance_id === instanceId);
      if (inst) {
        recordMove(instanceId, inst.position);
      }

      // Use the clicked mesh's position as the drag reference point.
      // dragState.offset is computed relative to meshPos, so handlePointerMove
      // produces target = meshPos + mouseDelta. We must use the same meshPos
      // as the start so that dragOffset starts at exactly [0,0,0].
      dragStartInstancePos.current = [...meshData.position];

      // Tell the store which instance is being dragged (offset starts at 0)
      setDragging(instanceId);

      // Add document-level listeners to track mouse even outside canvas
      document.addEventListener("mousemove", handlePointerMove);
      document.addEventListener("mouseup", handlePointerUp);

      // Select the instance
      setSelectedMesh(meshUuid);
      sendSelectInstance(instanceId);
    },
    [disabled, getPlaneIntersection, meshes, onDragStateChange, setSelectedMesh, sendSelectInstance, handlePointerMove, handlePointerUp, setDragging]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      document.removeEventListener("mousemove", handlePointerMove);
      document.removeEventListener("mouseup", handlePointerUp);
    };
  }, [handlePointerMove, handlePointerUp]);

  // Expose startDrag function via window for SceneMesh to use
  useEffect(() => {
    (window as { __startDrag?: typeof startDrag }).__startDrag = startDrag;
    return () => {
      delete (window as { __startDrag?: typeof startDrag }).__startDrag;
    };
  }, [startDrag]);

  return <>{children}</>;
}

function SceneContent({ isDragging, onIKDragStateChange, onEndCoordsDragStateChange }: { isDragging: boolean; onIKDragStateChange: (isDragging: boolean) => void; onEndCoordsDragStateChange: (isDragging: boolean) => void }) {
  const meshes = useMeshes();
  const ikEnabled = useIKEnabled();
  const ikGizmos = useIKGizmos();
  const hiddenGizmoIds = useHiddenGizmoIds();
  const highlightedMeshUuids = useHighlightedMeshUuids();
  const collidingMeshUuids = useCollidingMeshUuids();
  const endCoordsGizmoVisible = useEndCoordsGizmoVisible();
  const joints = useJoints();
  const visibleGizmos = ikGizmos.filter((g) => !hiddenGizmoIds[g.gizmo_id]);

  // Capture all joint angles at IK drag start for undo
  const handleIKDragStart = useCallback(() => {
    recordJointBatch(joints.map((j) => ({ name: j.name, value: j.current_angle })));
    onIKDragStateChange(true);
  }, [joints, onIKDragStateChange]);
  const handleIKDragEnd = useCallback(() => onIKDragStateChange(false), [onIKDragStateChange]);
  const handleEndCoordsDragStart = useCallback(() => onEndCoordsDragStateChange(true), [onEndCoordsDragStateChange]);
  const handleEndCoordsDragEnd = useCallback(() => onEndCoordsDragStateChange(false), [onEndCoordsDragStateChange]);

  // Create a Set for faster lookup
  const highlightedSet = new Set(highlightedMeshUuids);
  const collidingSet = new Set(collidingMeshUuids);

  return (
    <>
      <CameraController />
      <SceneUtilsProvider />

      {/* Lighting */}
      <ambientLight intensity={0.5} />
      <directionalLight position={[10, 10, 5]} intensity={1} castShadow />
      <directionalLight position={[-10, -10, -5]} intensity={0.3} />

      {/* Grid on XY plane (Z-up coordinate system) - raycast disabled */}
      <group raycast={() => null}>
        <Grid
          args={[10, 10]}
          cellSize={0.1}
          cellThickness={0.5}
          cellColor="#6e6e6e"
          sectionSize={1}
          sectionThickness={1}
          sectionColor="#9d4b4b"
          fadeDistance={30}
          fadeStrength={1}
          followCamera={false}
          infiniteGrid
          rotation={[Math.PI / 2, 0, 0]}
        />
      </group>

      {/* Render all meshes */}
      {Object.values(meshes).map((mesh) => (
        <SceneMesh
          key={mesh.uuid}
          mesh={mesh}
          isHighlighted={highlightedSet.has(mesh.uuid)}
          isColliding={collidingSet.has(mesh.uuid)}
        />
      ))}

      {/* Connection Point Markers */}
      <ConnectionPointMarkers />

      {/* IK Gizmos */}
      {ikEnabled && visibleGizmos.map((gizmo) => (
        <IKGizmo
          key={gizmo.gizmo_id}
          gizmo={gizmo}
          onDragStart={handleIKDragStart}
          onDragEnd={handleIKDragEnd}
        />
      ))}

      {/* End Coords Gizmo */}
      {endCoordsGizmoVisible && (
        <EndCoordsGizmo
          onDragStart={handleEndCoordsDragStart}
          onDragEnd={handleEndCoordsDragEnd}
        />
      )}

      {/* Joint Axis Visualization */}
      <JointAxisVisualization />

      {/* Controls with Z-up - disabled during drag */}
      <OrbitControls makeDefault enabled={!isDragging} />
    </>
  );
}

interface ContextMenuState {
  instanceId: string | null;  // null = right-clicked on empty space
  x: number;
  y: number;
}

interface ContextMenuItemProps {
  label: string;
  shortcut?: string;
  color?: string;
  hoverColor?: string;
  onClick: () => void;
}

function ContextMenuItem({ label, shortcut, color, hoverColor, onClick }: ContextMenuItemProps) {
  const defaultHover = "var(--mantine-color-default-hover, #f1f3f5)";
  return (
    <div
      style={{
        padding: "6px 12px",
        cursor: "pointer",
        fontSize: 13,
        color: color || "inherit",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 16,
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = hoverColor || defaultHover; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
      onClick={onClick}
    >
      <span>{label}</span>
      {shortcut && (
        <span style={{ fontSize: 11, color: "var(--mantine-color-dimmed, #868e96)" }}>
          {shortcut}
        </span>
      )}
    </div>
  );
}

export function Scene() {
  const backgroundColor = useBackgroundColor();
  const [isDragging, setIsDragging] = useState(false);
  const [isIKDragging, setIsIKDragging] = useState(false);
  const [isEndCoordsDragging, setIsEndCoordsDragging] = useState(false);
  const endCoordsGizmoVisible = useEndCoordsGizmoVisible();
  const setSelectedMesh = useViewerStore((state) => state.setSelectedMesh);
  const ikEnabled = useIKEnabled();
  const setIKEnabled = useViewerStore((state) => state.setIKEnabled);
  const { sendSelectInstance, sendDeleteInstance, sendDisconnectInstance, sendAddModule, sendEnableIKMode } = useWebSocket();
  const instances = useInstances();
  const selectedInstanceId = useSelectedModuleInstanceId();

  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Combined dragging state
  const isAnyDragging = isDragging || isIKDragging || isEndCoordsDragging;

  // Deselect when clicking on empty space (not on any mesh)
  const handlePointerMissed = useCallback(() => {
    setSelectedMesh(null);
    sendSelectInstance(null);
  }, [setSelectedMesh, sendSelectInstance]);

  // Close context menu on any click outside the menu
  const contextMenuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClick = (e: PointerEvent) => {
      if (contextMenuRef.current && contextMenuRef.current.contains(e.target as Node)) {
        return; // Click inside menu — don't close
      }
      setContextMenu(null);
    };

    document.addEventListener("pointerdown", handleClick);
    return () => {
      document.removeEventListener("pointerdown", handleClick);
    };
  }, []);

  // Listen for right-click events from SceneMesh
  useEffect(() => {
    const handleMeshContextMenu = (e: Event) => {
      const { instanceId, clientX, clientY } = (e as CustomEvent).detail;
      const rect = containerRef.current?.getBoundingClientRect();
      if (rect) {
        setContextMenu({
          instanceId,
          x: clientX - rect.left,
          y: clientY - rect.top,
        });
      }
    };

    window.addEventListener("mesh-context-menu", handleMeshContextMenu);
    return () => {
      window.removeEventListener("mesh-context-menu", handleMeshContextMenu);
    };
  }, []);

  // Right-click on empty space shows scene context menu
  const handleNativeContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    // Only show scene menu if no module context menu is already open
    // and the click wasn't on a mesh (mesh right-clicks are handled separately)
    if (!contextMenu) {
      const rect = containerRef.current?.getBoundingClientRect();
      if (rect) {
        setContextMenu({
          instanceId: null,
          x: e.clientX - rect.left,
          y: e.clientY - rect.top,
        });
      }
    }
  }, [contextMenu]);

  const handleDelete = useCallback((targetInstanceId?: string) => {
    const deleteId = targetInstanceId || contextMenu?.instanceId;
    if (!deleteId) return;
    const inst = instances.find((i) => i.instance_id === deleteId);
    if (inst) {
      recordDelete(inst, instances);
    }
    sendDeleteInstance(deleteId);
    setContextMenu(null);
  }, [contextMenu, instances, sendDeleteInstance]);

  const handleDisconnect = useCallback(() => {
    if (!contextMenu?.instanceId) return;
    const inst = instances.find((i) => i.instance_id === contextMenu.instanceId);
    if (inst && inst.parent_instance_id && inst.parent_link_name && inst.child_link_name) {
      recordDisconnect(inst.instance_id, inst.child_link_name, inst.parent_instance_id, inst.parent_link_name);
    }
    sendDisconnectInstance(contextMenu.instanceId);
    setContextMenu(null);
  }, [contextMenu, instances, sendDisconnectInstance]);

  const handleDuplicate = useCallback(() => {
    if (!contextMenu) return;
    const instance = instances.find((inst) => inst.instance_id === contextMenu.instanceId);
    if (!instance) return;

    // Find the module_id from the module_name (they are the same in this app)
    const pos = instance.position;
    const offset: [number, number, number] = [
      pos[0] + 0.1,
      pos[1] + 0.1,
      pos[2],
    ];
    sendAddModule(instance.module_name, offset);
    setContextMenu(null);
  }, [contextMenu, instances, sendAddModule]);

  // Check if the right-clicked instance has a parent (is connected)
  const contextInstance = contextMenu
    ? instances.find((inst) => inst.instance_id === contextMenu.instanceId)
    : null;
  const isConnected = contextInstance?.parent_instance_id != null;

  // For empty-space menu: check if there's a currently selected instance
  const selectedInstance = instances.find((inst) => inst.instance_id === selectedInstanceId);
  const isSelectedConnected = selectedInstance?.parent_instance_id != null;

  // Detect platform for shortcut display
  const isMac = navigator.platform.toUpperCase().indexOf("MAC") >= 0;
  const modKey = isMac ? "\u2318" : "Ctrl+";

  return (
    <div ref={containerRef} style={{ width: "100%", height: "100%", position: "relative" }} onContextMenu={handleNativeContextMenu}>
      <Canvas
        camera={{ position: [2, 2, 2], fov: 50, up: [0, 0, 1] }}
        style={{ width: "100%", height: "100%" }}
        gl={{ antialias: true }}
        onPointerMissed={handlePointerMissed}
      >
        <color attach="background" args={[backgroundColor]} />
        <DragHandler onDragStateChange={setIsDragging} disabled={endCoordsGizmoVisible}>
          <SceneContent isDragging={isAnyDragging} onIKDragStateChange={setIsIKDragging} onEndCoordsDragStateChange={setIsEndCoordsDragging} />
        </DragHandler>
      </Canvas>

      {/* Right-click context menu */}
      {contextMenu && (
        <div
          ref={contextMenuRef}
          style={{
            position: "absolute",
            left: contextMenu.x,
            top: contextMenu.y,
            zIndex: 1000,
            background: "var(--mantine-color-body, #fff)",
            border: "1px solid var(--mantine-color-default-border, #dee2e6)",
            borderRadius: 4,
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            padding: "4px 0",
            minWidth: 170,
          }}
        >
          {contextMenu.instanceId ? (
            <>
              {/* Module context menu */}
              {contextInstance && (
                <div style={{
                  padding: "4px 12px",
                  fontSize: 12,
                  color: "var(--mantine-color-dimmed, #868e96)",
                  borderBottom: "1px solid var(--mantine-color-default-border, #dee2e6)",
                  marginBottom: 2,
                }}>
                  {contextInstance.module_name}
                </div>
              )}
              <ContextMenuItem
                label="Duplicate"
                shortcut={`${modKey}D`}
                onClick={handleDuplicate}
              />
              {isConnected && (
                <ContextMenuItem
                  label="Disconnect"
                  onClick={handleDisconnect}
                />
              )}
              <div style={{ height: 1, background: "var(--mantine-color-default-border, #dee2e6)", margin: "2px 0" }} />
              <ContextMenuItem
                label="Delete"
                shortcut={isMac ? "\u232B" : "Del"}
                color="var(--mantine-color-red-6, #e03131)"
                hoverColor="var(--mantine-color-red-light-hover, #ffe3e3)"
                onClick={() => handleDelete()}
              />
            </>
          ) : (
            <>
              {/* Scene context menu (right-click on empty space) */}
              <ContextMenuItem
                label={ikEnabled ? "Disable IK Mode" : "Enable IK Mode"}
                onClick={() => {
                  const newEnabled = !ikEnabled;
                  setIKEnabled(newEnabled);
                  sendEnableIKMode(newEnabled);
                  setContextMenu(null);
                }}
              />
              {selectedInstance && (
                <>
                  <div style={{ height: 1, background: "var(--mantine-color-default-border, #dee2e6)", margin: "2px 0" }} />
                  <div style={{
                    padding: "4px 12px",
                    fontSize: 12,
                    color: "var(--mantine-color-dimmed, #868e96)",
                    borderBottom: "1px solid var(--mantine-color-default-border, #dee2e6)",
                    marginBottom: 2,
                  }}>
                    {selectedInstance.module_name}
                  </div>
                  <ContextMenuItem
                    label="Duplicate"
                    shortcut={`${modKey}D`}
                    onClick={() => {
                      const pos = selectedInstance.position;
                      sendAddModule(selectedInstance.module_name, [pos[0] + 0.1, pos[1] + 0.1, pos[2]]);
                      setContextMenu(null);
                    }}
                  />
                  {isSelectedConnected && (
                    <ContextMenuItem
                      label="Disconnect"
                      onClick={() => {
                        if (selectedInstance.parent_instance_id && selectedInstance.parent_link_name && selectedInstance.child_link_name) {
                          recordDisconnect(selectedInstance.instance_id, selectedInstance.child_link_name, selectedInstance.parent_instance_id, selectedInstance.parent_link_name);
                        }
                        sendDisconnectInstance(selectedInstance.instance_id);
                        setContextMenu(null);
                      }}
                    />
                  )}
                  <div style={{ height: 1, background: "var(--mantine-color-default-border, #dee2e6)", margin: "2px 0" }} />
                  <ContextMenuItem
                    label="Delete"
                    shortcut={isMac ? "\u232B" : "Del"}
                    color="var(--mantine-color-red-6, #e03131)"
                    hoverColor="var(--mantine-color-red-light-hover, #ffe3e3)"
                    onClick={() => handleDelete(selectedInstance.instance_id)}
                  />
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
